import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Vote, Building2, Mail, Lock, ArrowRight, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { register } from "../lib/api";
import { LanguageSelector } from "../components/LanguageSelector";
import { PasswordStrength } from "../components/PasswordStrength";
import { useAppConfig } from "../lib/AppConfigContext";

export default function RegisterPage() {
  const { t } = useTranslation();
  const { appName } = useAppConfig();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Verification email sent state
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(t('auth.passwordsDontMatch'));
      return;
    }

    // Validate password policy
    const hasMinLen = password.length >= 10;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNum = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()\-_=+\[\]{};:'",.<>?/\\|`~]/.test(password);
    if (!hasMinLen || !hasUpper || !hasLower || !hasNum || !hasSpecial) {
      toast.error(t('passwordPolicy.weak'));
      return;
    }

    setLoading(true);

    try {
      const lang = localStorage.getItem("i18nextLng") || "ca";
      const response = await register(name, email, password, lang);
      setSentEmail(email);
      setEmailSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.registerEntity'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await import("../lib/api").then(m => m.default.post("/auth/resend-verification", { email: sentEmail }));
      toast.success(t('register.resendSuccess'));
    } catch {
      toast.error(t('errors.generic'));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1647705985473-968ff327b7a8?auto=format&fit=crop&w=2000&q=80"
          alt="Votació"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h2 className="text-4xl font-heading font-bold mb-4">
              {t('register.heroTitle')}
            </h2>
            <p className="text-lg text-slate-300 max-w-md">
              {t('register.heroSubtitle')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form or Email Sent */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSelector />
        </div>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-slate-900 rounded-xl flex items-center justify-center">
              <Vote className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-slate-900">{appName}</h1>
              <p className="text-sm text-slate-500">{t('common.secureVotingSystem')}</p>
            </div>
          </div>

          {emailSent ? (
            /* Email Verification Sent */
            <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm text-center" data-testid="email-sent-panel">
              <Mail className="h-14 w-14 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
                {t('register.verificationEmailSent')}
              </h2>
              <p className="text-slate-500 mb-4">{t('register.verificationEmailSentDesc')}</p>
              <p className="text-sm text-slate-700 mb-6">
                {t('register.verificationEmailSentTo')} <strong>{sentEmail}</strong>
              </p>
              <Button
                variant="outline"
                onClick={handleResend}
                disabled={resending}
                className="w-full mb-3"
                data-testid="resend-verification-btn"
              >
                {resending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                {t('register.resendVerification')}
              </Button>
              <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">
                {t('register.goToLogin')}
              </Link>
            </div>
          ) : (
          /* Registration Form */
          <>
          <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
            <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">
              {t('register.title')}
            </h2>
            <p className="text-slate-500 mb-6">
              {t('register.subtitle')}
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="form-group">
                <Label htmlFor="name" className="form-label">
                  {t('register.entityName')}
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 h-11"
                    placeholder={t('register.entityPlaceholder')}
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="email" className="form-label">
                  {t('register.email')}
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11"
                    placeholder={t('register.emailPlaceholder')}
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="form-group">
                <Label htmlFor="password" className="form-label">
                  {t('register.password')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-11"
                    placeholder={t('register.passwordPlaceholder')}
                    required
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <PasswordStrength password={password} minLength={10} />
              </div>

              <div className="form-group">
                <Label htmlFor="confirmPassword" className="form-label">
                  {t('register.confirmPassword')}
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 h-11"
                    placeholder={t('register.repeatPassword')}
                    required
                    data-testid="register-confirm-password"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 btn-hover"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <div className="spinner" />
                ) : (
                  <>
                    {t('register.submit')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <p className="text-center mt-6 text-slate-600">
            {t('register.haveAccount')}{" "}
            <Link
              to="/login"
              className="text-blue-600 hover:text-blue-700 font-medium"
              data-testid="login-link"
            >
              {t('register.login')}
            </Link>
          </p>
          </>
          )}
        </motion.div>
      </div>
    </div>
  );
}
